import { type SqliteRemoteDatabase, drizzle } from 'drizzle-orm/sqlite-proxy';
import { SQLocalDrizzle } from 'sqlocal/drizzle';

import { Bench } from '$lib/bench';
import * as schema from '$lib/client/db/schema';

import { migrate } from './db/migrate';
import { seed } from './db/seed';

export class SQLocalRepo {
	db: SqliteRemoteDatabase<typeof schema>;

	private readonly _ctx: SQLocalDrizzle;

	constructor({
		onError,
		onInit,
		onConnect,
		onReady
	}: {
		onError: (error: Error) => void;
		onInit: () => void;
		onConnect: (reason: string) => void;
		onReady: () => void;
	}) {
		const ctx = new SQLocalDrizzle({
			databasePath: 'quill.sqlite3',

			onInit: (sql) => {
				Bench.mark('db.init');
				onInit();

				// See [Sensible SQLite defaults](https://briandouglas.ie/sqlite-defaults/)
				return [
					sql`PRAGMA journal_mode = WAL;`,
					sql`PRAGMA synchronous = NORMAL;`,
					sql`PRAGMA busy_timeout = 5000;`,
					sql`PRAGMA cache_size = -20000;`,

					// currently disabled, slows down initialization by 200ms
					// sql`PRAGMA auto_vacuum = INCREMENTAL;`,

					sql`PRAGMA temp_store = MEMORY;`,
					sql`PRAGMA mmap_size = 2147483648;`,
					sql`PRAGMA page_size = 8192;`,

					// required for making the drizzle migrations work out
					sql`PRAGMA foreign_keys = OFF;`
				];
			},

			onConnect: async (reason) => {
				Bench.measure('db.connect', 'db.init');
				Bench.mark('db.connect');

				try {
					onConnect(reason);

					Bench.mark('db.migrate');
					await migrate(ctx);
					Bench.measure('db.migrate');

					Bench.mark('db.seed');
					await seed(ctx.transaction, db);
					Bench.measure('db.seed');

					Bench.mark('db.ready');
					onReady();
				} catch (cause) {
					const error = new Error('Failed to prepare client repository', {
						cause
					});
					onError(error);
				}
			}
		});

		this._ctx = ctx;

		const db = drizzle(ctx.driver, ctx.batchDriver, { schema });
		this.db = db;
	}

	export() {
		return this._ctx.getDatabaseFile();
	}

	import(databaseFile: File) {
		return this._ctx.overwriteDatabaseFile(databaseFile);
	}

	purge() {
		return this._ctx.deleteDatabaseFile();
	}
}
